import {
  mdiAccountOutline,
  mdiAlertCircleOutline,
  mdiCalendarSync,
  mdiCheckCircleOutline,
  mdiClipboardTextClockOutline,
  mdiCloseCircleOutline,
  mdiDatabaseSyncOutline,
  mdiEmailOutline,
  mdiGoogle,
  mdiHelpCircleOutline,
  mdiHomeOutline,
  mdiInboxArrowDownOutline,
  mdiMicrosoftOutlook,
  mdiPhoneOutline,
  mdiRobotOutline,
} from "@mdi/js"
import {
  buildRichTooltip,
  tooltipField,
  tooltipTitle,
} from "../../../../../../../../../../../_shared/_logic/rich-tooltips"

export type InboxTagPresentation = {
  label: string
  subLabel?: string | null
  color: string
  icon: string
  tooltip: string
}

export const cleanText = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

export const compactInboxCustomerToggleLabel = (displayName: string, maxLetters = 5): string => {
  const normalized = cleanText(displayName) || "Ansprechpartner"
  const firstName = normalized.split(/\s+/)[0] || normalized
  const compact = Array.from(firstName).slice(0, Math.max(1, maxLetters)).join("")

  return normalized === compact ? compact : `${compact}…`
}

export const emptyLabel = "—"

export const extractEmail = (value: string | null | undefined): string => {
  const match = `${value ?? ""}`.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)

  return match?.[0] ?? ""
}

export const extractPhone = (value: string | null | undefined): string => {
  const match = `${value ?? ""}`.match(/(?:\+|00)?[0-9][0-9\s()/.-]{5,}[0-9]/)

  return match?.[0]?.trim() ?? ""
}

export const buildInboxContactSubLabel = (
  roleLabel: string | null | undefined,
  phone: string | null | undefined,
): string | null => {
  const parts = [cleanText(roleLabel), cleanText(phone)].filter(Boolean)

  return parts.length ? parts.join(" · ") : null
}

export const formatInboxSourceLabel = (label: string): string => {
  const normalized = cleanText(label)

  if (!normalized) return "Inbox"

  const lower = normalized.toLowerCase()

  if (lower.startsWith("bot_appointments.") || lower.includes("voice") || lower.includes("chat")) {
    return "Terminbot"
  }

  if (lower.startsWith("appointment_portal.") || lower.includes("terminportal")) {
    return "Terminportal"
  }

  if (lower.includes("google")) return "Google Kalender"

  if (lower.includes("microsoft") || lower.includes("outlook")) return "Microsoft Outlook"

  if (lower.includes("calendar")) return "Kalenderanbieter"

  if (lower.includes("import") || lower.includes("fachprogramm") || lower.includes("kehrbezirk")) {
    return "Kehrbezirksprogramm"
  }

  if (/^[a-z0-9_.-]+$/.test(lower) && /[._]/.test(lower)) return "Inbox"

  return normalized
}

const sourcePresentation = (
  label: string,
): Pick<InboxTagPresentation, "color" | "icon" | "subLabel"> => {
  const normalized = label.toLowerCase()

  if (normalized.includes("google")) return { color: "blue", icon: mdiGoogle, subLabel: "Kalender" }

  if (normalized.includes("microsoft") || normalized.includes("outlook")) {
    return { color: "blue", icon: mdiMicrosoftOutlook, subLabel: "Kalender" }
  }

  if (normalized.includes("kalender"))
    return { color: "blue", icon: mdiCalendarSync, subLabel: "Synchronisierung" }

  if (normalized.includes("portal") || normalized.includes("termin")) {
    return { color: "teal", icon: mdiInboxArrowDownOutline, subLabel: "Rückmeldung" }
  }

  if (normalized.includes("bot") || normalized.includes("voice") || normalized.includes("chat")) {
    return { color: "violet", icon: mdiRobotOutline, subLabel: "Bot" }
  }

  if (
    normalized.includes("import") ||
    normalized.includes("fach") ||
    normalized.includes("quelle")
  ) {
    return { color: "blue", icon: mdiDatabaseSyncOutline, subLabel: "Kehrbezirksprogramm" }
  }

  return { color: "#64748b", icon: mdiHelpCircleOutline, subLabel: "Inbox" }
}

export const buildInboxSourceTag = (
  label: string,
  subLabel?: string | null,
): InboxTagPresentation => {
  const resolvedLabel = formatInboxSourceLabel(label)
  const presentation = sourcePresentation(resolvedLabel)
  const resolvedSubLabel = cleanText(subLabel) || presentation.subLabel

  return {
    label: resolvedLabel,
    color: presentation.color,
    icon: presentation.icon,
    subLabel: resolvedSubLabel,
    tooltip: buildRichTooltip([
      tooltipTitle("Herkunft"),
      tooltipField("Herkunft", resolvedLabel),
      tooltipField("Einordnung", resolvedSubLabel),
    ]),
  }
}

export type InboxStatusTagOptions = {
  detail?: string | null
  hint?: string | null
  subLabel?: string | null
}

const statusTooltip = (label: string, options: InboxStatusTagOptions = {}) =>
  buildRichTooltip([
    tooltipTitle("Status"),
    tooltipField("Stand", label),
    tooltipField("Grund", options.detail),
    tooltipField("Nächster Schritt", options.hint),
  ])

export const buildInboxStatusTag = (
  label: string,
  options: InboxStatusTagOptions = {},
): InboxTagPresentation => {
  const resolvedLabel = cleanText(label) || "Offen"
  const subLabel = cleanText(options.subLabel)
  const normalized = resolvedLabel.toLowerCase()

  if (
    normalized.includes("erledigt") ||
    normalized.includes("abgeschlossen") ||
    normalized.includes("resolved")
  ) {
    return {
      label: "Erledigt",
      subLabel: subLabel || undefined,
      color: "emerald",
      icon: mdiCheckCircleOutline,
      tooltip: statusTooltip(resolvedLabel, options),
    }
  }

  if (
    normalized.includes("warnung") ||
    normalized.includes("übersprung") ||
    normalized.includes("skip") ||
    normalized.includes("nicht ausgeführt")
  ) {
    return {
      label: "Warnung",
      subLabel: subLabel || undefined,
      color: "orange",
      icon: mdiAlertCircleOutline,
      tooltip: statusTooltip(resolvedLabel, options),
    }
  }

  if (
    normalized.includes("prüfung") ||
    normalized.includes("nacharbeit") ||
    normalized.includes("kompatibel") ||
    normalized.includes("fehler") ||
    normalized.includes("fehl")
  ) {
    return {
      label: "Prüfung nötig",
      subLabel: subLabel || undefined,
      color: "rose",
      icon: mdiCloseCircleOutline,
      tooltip: statusTooltip(resolvedLabel, options),
    }
  }

  return {
    label: resolvedLabel,
    subLabel: subLabel || undefined,
    color: "amber",
    icon: mdiClipboardTextClockOutline,
    tooltip: statusTooltip(resolvedLabel, options),
  }
}

export const contactPointIcon = (kind: "email" | "phone") =>
  kind === "email" ? mdiEmailOutline : mdiPhoneOutline

export const contactPointLabel = (kind: "email" | "phone") =>
  kind === "email" ? "E-Mail" : "Telefon"

export const unitIcon = mdiHomeOutline
export const employeeIcon = mdiAccountOutline
